import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Facebook, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";

const socialLinks = [
  {
    label: "Facebook (personal)",
    href: "https://www.facebook.com/markntammy.richard",
    icon: Facebook,
    badge: "FB",
  },
  {
    label: "Facebook (Tammy Richard KW)",
    href: "https://www.facebook.com/tammyricharKW",
    icon: Facebook,
    badge: "KW",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/tammyrichard27/",
    icon: Instagram,
    badge: null,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/tammyrichard/",
    icon: Linkedin,
    badge: null,
  },
];

const primaryLinks = [
  { to: "/coaching", label: "Coaching" },
  { to: "/real-estate", label: "Real Estate" },
  { to: "/lifestyles", label: "Lifestyles" },
  { to: "/recruiting", label: "Join us" },
];

const secondaryLinks = [
  { to: "/book", label: "The Book" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/coaching-resources", label: "Agent Portal" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header
        className={`sticky top-0 z-50 transition-all ${
          scrolled
            ? "bg-background/85 backdrop-blur-xl border-b border-border"
            : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-baseline gap-3 shrink-0" data-testid="nav-logo">
            <span className="font-serif text-2xl tracking-tight">Tammy Richard</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 flex-1 justify-center">
            {primaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? "text-primary" : "text-foreground/75 hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <span className="text-foreground/20">|</span>
            {secondaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.15em] transition-colors ${
                    isActive ? "text-primary" : "text-foreground/60 hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <Link to="/booking" data-testid="nav-book-cta" className="hidden lg:block shrink-0">
            <Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-lift px-6">
              Book a Call
            </Button>
          </Link>

          <button
            className="lg:hidden p-2 rounded-full hover:bg-muted transition"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-2">
              {[...primaryLinks, ...secondaryLinks].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `py-2 text-base font-medium ${isActive ? "text-primary" : "text-foreground"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/booking" data-testid="mobile-nav-book-cta">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90 mt-2">
                  Book a Call
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-[#0F1E33] text-[#F5F8FB] mt-24" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <h3 className="font-serif text-4xl mb-4">Let's Connect.</h3>
            <p className="text-sm leading-relaxed opacity-80 max-w-sm">
              Coaching, conversation, and resources for a thriving life —
              wherever you are on the journey.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-6" data-testid="footer-socials">
              {socialLinks.map((s) => (
                <a
                  key={s.href}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="relative w-11 h-11 rounded-full border border-white/15 flex items-center justify-center hover:bg-primary hover:border-primary hover:text-white transition group"
                >
                  <s.icon size={17} strokeWidth={1.6} />
                  {s.badge && (
                    <span className="absolute -bottom-1 -right-1 bg-primary text-white text-[8px] font-medium px-1.5 py-0.5 rounded-full">
                      {s.badge}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] opacity-60 mb-4">Explore</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/coaching" className="hover:text-[#93C5FD] transition">Coaching</Link></li>
              <li><Link to="/producer-coaching" className="hover:text-[#93C5FD] transition">Producer Coaching</Link></li>
              <li><Link to="/lifestyles" className="hover:text-[#93C5FD] transition">Lifestyles</Link></li>
              <li><Link to="/real-estate" className="hover:text-[#93C5FD] transition">Real Estate</Link></li>
              <li><Link to="/testimonials" className="hover:text-[#93C5FD] transition">Testimonials</Link></li>
              <li><Link to="/lifestyles/grow-your-mind" className="hover:text-[#93C5FD] transition">Grow Your Mind</Link></li>
              <li><Link to="/book" className="hover:text-[#93C5FD] transition">The Book</Link></li>
              <li><Link to="/join" className="hover:text-[#93C5FD] transition">Join us</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] opacity-60 mb-4">
              Reach Tammy
            </p>
            <ul className="space-y-2 text-sm opacity-90">
              <li><a href="mailto:trichard@kw.com" className="hover:text-[#93C5FD] transition">trichard@kw.com</a></li>
              <li><a href="tel:+12078380342" className="hover:text-[#93C5FD] transition">(207) 838-0342 · mobile</a></li>
              <li><a href="tel:+12075532427" className="hover:text-[#93C5FD] transition">(207) 553-2427 · office</a></li>
              <li className="pt-2 opacity-75">Keller Williams Maine</li>
              <li className="opacity-75">Greater Portland, Maine</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row justify-between gap-2 text-xs opacity-60">
            <span>© {new Date().getFullYear()} Tammy Richard. All rights reserved.</span>
            <span>Made with care.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
